/**
 * Module selector component.
 *
 * Displays exam modules (Reading, Listening, Writing, Speaking) for selection.
 * Allows multi-select with at least one module required.
 *
 * @module ModuleSelector
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Book, Headphones, Pen, Mic, AlertCircle, Lock } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ExamModule } from '@/types/exam-simulation';
import type { MicrophonePermissionState } from '@/hooks/useMicrophonePermission';
import { useTranslation } from 'react-i18next';
import { useConsentStatus } from '@/hooks/useConsentStatus';
import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/lib/routeConstants';
import {
  canStartSimulation,
  SIMULATION_RENEWAL_BLOCK_MESSAGE,
} from '@/lib/planAccess';

interface ModuleOption {
  id: ExamModule;
  icon: typeof Book;
  color: string;
}

interface ModuleSelectorProps {
  readonly selectedModules: ExamModule[];
  readonly onToggle: (module: ExamModule) => void;
  readonly onConfirm: () => void | Promise<void>;
  readonly microphonePermission?: MicrophonePermissionState;
  readonly onRequestMicrophonePermission?: () => Promise<boolean>;
  readonly isMicrophoneSupported?: boolean;
}

const modules: ModuleOption[] = [
  {
    id: 'reading',
    icon: Book,
    color:
      'text-exam-focus-border bg-exam-focus-border/10 border-exam-focus-border/20',
  },
  {
    id: 'listening',
    icon: Headphones,
    color:
      'text-exam-audio-wave bg-exam-audio-wave/10 border-exam-audio-wave/20',
  },
  {
    id: 'writing',
    icon: Pen,
    color: 'text-exam-success bg-exam-success/10 border-exam-success/20',
  },
  {
    id: 'speaking',
    icon: Mic,
    color: 'text-exam-recording bg-exam-recording/10 border-exam-recording/20',
  },
];

export function ModuleSelector({
  selectedModules,
  onToggle,
  onConfirm,
  microphonePermission = 'unknown',
  onRequestMicrophonePermission,
  isMicrophoneSupported = true,
}: ModuleSelectorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const isAdmin = user?.userType === 'admin';

  const {
    hasVoiceConsent,
    isLoading: isConsentLoading,
    isError: isConsentError,
  } = useConsentStatus();
  const voiceConsentDenied =
    isConsentLoading || isConsentError || !hasVoiceConsent;

  // Plan entitlement: a module may be unavailable because the tier doesn't
  // include it (e.g. Free = Reading + Listening) OR because its per-module
  // attempt quota is used up (e.g. 3/3 reading on Starter). Each module is
  // gated independently. While loading we allow all to avoid flicker.
  const { plan, isLoading: isPlanLoading } = useCurrentPlan(!isAdmin);
  const simulationAllowed = canStartSimulation(plan, isAdmin);
  const allowedModules = plan?.limits.allowed_modules ?? null;
  const moduleQuota = plan?.mock_quota ?? null; // null = unlimited
  const moduleUsage = useMemo(() => plan?.module_usage ?? {}, [plan?.module_usage]);

  const isModuleNotInTier = (module: ExamModule): boolean =>
    !isAdmin && allowedModules !== null && !allowedModules.includes(module);
  const isModuleQuotaReached = (module: ExamModule): boolean =>
    !isAdmin &&
    moduleQuota !== null &&
    (moduleUsage[module] ?? 0) >= moduleQuota;
  const isModulePlanBlocked = (module: ExamModule): boolean =>
    isModuleNotInTier(module) || isModuleQuotaReached(module);

  const blockedSelectedModule = useMemo(() => {
    if (!plan || isAdmin) {
      return null;
    }
    return (
      selectedModules.find(
        (module) =>
          (allowedModules !== null && !allowedModules.includes(module)) ||
          (moduleQuota !== null && (moduleUsage[module] ?? 0) >= moduleQuota),
      ) ?? null
    );
  }, [
    plan,
    isAdmin,
    selectedModules,
    allowedModules,
    moduleQuota,
    moduleUsage,
  ]);

  // Deselect any plan-blocked module that remains selected (e.g. restored draft).
  useEffect(() => {
    if (blockedSelectedModule && selectedModules.length > 1) {
      onToggle(blockedSelectedModule);
    }
  }, [blockedSelectedModule, selectedModules.length, onToggle]);

  const hasSpeakingModule = selectedModules.includes('speaking');
  const canConfirm =
    !isPlanLoading &&
    simulationAllowed &&
    selectedModules.length > 0 &&
    !(hasSpeakingModule && voiceConsentDenied);

  // Auto-deselect speaking if voice consent has been revoked while it was selected
  useEffect(() => {
    if (
      voiceConsentDenied &&
      !isConsentLoading &&
      hasSpeakingModule &&
      selectedModules.length > 1
    ) {
      onToggle('speaking');
    }
  }, [
    voiceConsentDenied,
    isConsentLoading,
    hasSpeakingModule,
    selectedModules.length,
    onToggle,
  ]);

  if (!isAdmin && !isPlanLoading && !simulationAllowed) {
    return (
      <div className="mx-auto max-w-lg space-y-4 px-4 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
          <Lock className="h-6 w-6" aria-hidden />
        </div>
        <h2 className="font-ui text-2xl font-bold text-foreground">
          {t('moduleSelection.renewRequired.title', { defaultValue: 'Pass expired' })}
        </h2>
        <p className="font-ui text-sm text-muted-foreground">
          {t('moduleSelection.renewRequired.description', {
            defaultValue: SIMULATION_RENEWAL_BLOCK_MESSAGE,
          })}
        </p>
        <Button
          type="button"
          className="font-ui"
          onClick={() => navigate(`${ROUTES.SETTINGS}#plan`)}
        >
          {t('dashboard.planBanner.expired.cta', { defaultValue: 'Renew plan' })}
        </Button>
      </div>
    );
  }

  const handleToggle = (module: ExamModule) => {
    // Block selecting modules not included in the user's plan
    if (isModulePlanBlocked(module) && !selectedModules.includes(module)) {
      return;
    }
    // Block selecting speaking if voice consent is denied
    if (
      module === 'speaking' &&
      voiceConsentDenied &&
      !selectedModules.includes('speaking')
    ) {
      return;
    }
    // Prevent deselecting if it's the last selected module
    if (selectedModules.includes(module) && selectedModules.length <= 1) {
      return;
    }
    onToggle(module);
  };

  const handleRequestPermission = async () => {
    if (!onRequestMicrophonePermission) return;

    setIsRequestingPermission(true);
    const granted = await onRequestMicrophonePermission();
    setIsRequestingPermission(false);

    // If permission was denied and speaking is selected, deselect it
    if (!granted && selectedModules.includes('speaking')) {
      // Only deselect if there are other modules selected
      if (selectedModules.length > 1) {
        onToggle('speaking');
      }
    }
  };

  const handleConfirm = async () => {
    // Block confirmation if voice consent is denied and speaking is still selected
    if (hasSpeakingModule && voiceConsentDenied) {
      return;
    }

    // Request microphone permission if speaking module is selected and permission not yet granted
    if (
      hasSpeakingModule &&
      microphonePermission !== 'granted' &&
      isMicrophoneSupported &&
      onRequestMicrophonePermission
    ) {
      setIsRequestingPermission(true);
      const granted = await onRequestMicrophonePermission();
      setIsRequestingPermission(false);

      if (!granted) {
        // Permission denied - automatically deselect speaking module
        if (selectedModules.includes('speaking')) {
          onToggle('speaking');
        }
        // Don't proceed if this was the only module selected
        if (
          selectedModules.length === 1 &&
          selectedModules.includes('speaking')
        ) {
          return;
        }
      }
    }

    // Call the parent's onConfirm handler
    await onConfirm();
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="text-center px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
          {t('moduleSelection.title', { defaultValue: 'Select Modules' })}
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground max-w-lg mx-auto">
          {t('moduleSelection.subtitle', {
            defaultValue: 'Choose one or more exam modules to practice',
          })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {modules.map((module) => {
          const Icon = module.icon;
          const isSelected = selectedModules.includes(module.id);
          const isLastSelected = isSelected && selectedModules.length === 1;
          const isSpeakingBlocked =
            module.id === 'speaking' && voiceConsentDenied;
          const isPlanBlocked = isModulePlanBlocked(module.id);
          const isBlocked = isSpeakingBlocked || isPlanBlocked;

          return (
            <Card
              key={module.id}
              role="button"
              tabIndex={isBlocked ? -1 : 0}
              aria-disabled={isBlocked}
              aria-pressed={isSelected}
              className={cn(
                'relative transition-all duration-200 overflow-hidden',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                isBlocked
                  ? 'cursor-not-allowed opacity-60 border-border bg-muted/30'
                  : 'cursor-pointer hover:shadow-lg',
                !isBlocked && isSelected
                  ? 'border-primary ring-1 ring-primary/20 bg-primary/[0.02]'
                  : !isBlocked &&
                      'border-border hover:border-primary/50 bg-card',
                isLastSelected && 'opacity-100',
              )}
              onClick={() => {
                if (isBlocked) return;
                handleToggle(module.id);
              }}
              onKeyDown={(e) => {
                if (!isBlocked && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleToggle(module.id);
                }
              }}
            >
              <CardHeader className="relative p-5">
                <div className="absolute top-5 right-5">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggle(module.id)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isLastSelected || isBlocked}
                    tabIndex={-1}
                    aria-hidden
                    className="h-5 w-5"
                  />
                </div>
                <div className="flex items-start gap-4 pr-10">
                  <div
                    className={cn(
                      'w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200',
                      module.color,
                      isSelected && 'scale-105',
                    )}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base sm:text-lg font-bold mb-1 truncate">
                      {t(`moduleSelection.modules.${module.id}.label`)}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm line-clamp-2 leading-relaxed">
                      {t(`moduleSelection.modules.${module.id}.description`)}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="px-5 pb-5 pt-0">
                {isLastSelected && (
                  <p className="text-[10px] sm:text-xs font-medium text-muted-foreground/80 mb-2 italic">
                    {t('moduleSelection.atLeastOne', {
                      defaultValue: 'At least one module must be selected',
                    })}
                  </p>
                )}
                {isPlanBlocked && (
                  <div className="mt-2 space-y-2">
                    <Badge
                      variant="outline"
                      className="text-amber-700 border-amber-600 bg-amber-50/60 text-[10px] sm:text-xs"
                    >
                      <Lock className="w-3 h-3 mr-1" />
                      {isModuleQuotaReached(module.id) &&
                      !isModuleNotInTier(module.id)
                        ? t('moduleSelection.planLimitReached', {
                            defaultValue: 'Attempt limit reached',
                          })
                        : t('moduleSelection.planUpgradeRequired', {
                            defaultValue: 'Not included in your plan',
                          })}
                    </Badge>
                    <p className="text-[11px] sm:text-xs text-muted-foreground leading-relaxed">
                      {isModuleQuotaReached(module.id) &&
                      !isModuleNotInTier(module.id)
                        ? t('moduleSelection.planLimitPrefix', {
                            defaultValue:
                              "You've used all your attempts for this module on your current plan. Upgrade in",
                          })
                        : t('moduleSelection.planUpgradePrefix', {
                            defaultValue:
                              'This module is not included in your current plan. Upgrade in',
                          })}{' '}
                      <Link
                        to={`${ROUTES.SETTINGS}#plan`}
                        onClick={(e) => e.stopPropagation()}
                        className="underline text-primary font-medium"
                      >
                        {t('moduleSelection.planUpgradeLink', {
                          defaultValue: 'Settings',
                        })}
                      </Link>{' '}
                      {t('moduleSelection.planUpgradeSuffix', {
                        defaultValue: 'to unlock it.',
                      })}
                    </p>
                  </div>
                )}
                {module.id === 'speaking' &&
                  isSpeakingBlocked &&
                  !isPlanBlocked && (
                    <div className="mt-2 space-y-2">
                      <Badge
                        variant="outline"
                        className="text-amber-700 border-amber-600 bg-amber-50/60 text-[10px] sm:text-xs"
                      >
                        <Lock className="w-3 h-3 mr-1" />
                        {t('moduleSelection.voiceConsentRequired', {
                          defaultValue: 'Voice recording consent required',
                        })}
                      </Badge>
                      <p className="text-[11px] sm:text-xs text-muted-foreground leading-relaxed">
                        {t('consent.simulation.voiceDisabledMessage', {
                          defaultValue:
                            'Because of your privacy settings, this option is not available. To use this option, go to',
                        })}{' '}
                        <Link
                          to={`${ROUTES.SETTINGS}#privacy`}
                          onClick={(e) => e.stopPropagation()}
                          className="underline text-primary font-medium"
                        >
                          {t('consent.simulation.voiceDisabledLink', {
                            defaultValue: 'Settings',
                          })}
                        </Link>{' '}
                        {t('consent.simulation.voiceDisabledSuffix', {
                          defaultValue: 'and update your privacy preferences.',
                        })}
                      </p>
                    </div>
                  )}
                {module.id === 'speaking' &&
                  isSelected &&
                  !isSpeakingBlocked && (
                    <div className="mt-2 space-y-2">
                      {microphonePermission === 'unknown' &&
                        isMicrophoneSupported && (
                          <Badge
                            variant="outline"
                            className="text-yellow-600 border-yellow-600 bg-yellow-50/50 text-[10px] sm:text-xs"
                          >
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {t('moduleSelection.permissionRequested', {
                              defaultValue:
                                'Permission will be requested on confirm',
                            })}
                          </Badge>
                        )}
                      {microphonePermission === 'granted' && (
                        <Badge
                          variant="outline"
                          className="text-green-600 border-green-600 bg-green-50/50 text-[10px] sm:text-xs"
                        >
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {t('moduleSelection.micGranted', {
                            defaultValue: 'Microphone access granted',
                          })}
                        </Badge>
                      )}
                      {microphonePermission === 'denied' && (
                        <div className="space-y-1.5">
                          <Badge
                            variant="outline"
                            className="text-red-600 border-red-600 bg-red-50/50 text-[10px] sm:text-xs"
                          >
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {t('moduleSelection.micDenied', {
                              defaultValue: 'Microphone access denied',
                            })}
                          </Badge>
                          <p className="text-[10px] sm:text-xs text-muted-foreground font-medium">
                            {t('moduleSelection.micDeniedDesc', {
                              defaultValue:
                                'Speaking module will be deselected',
                            })}
                          </p>
                          {isMicrophoneSupported &&
                            onRequestMicrophonePermission && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRequestPermission();
                                }}
                                className="h-7 text-[10px] sm:text-xs font-bold"
                              >
                                {t('moduleSelection.retry', {
                                  defaultValue: 'Retry',
                                })}
                              </Button>
                            )}
                        </div>
                      )}
                      {!isMicrophoneSupported && (
                        <Badge
                          variant="outline"
                          className="text-muted-foreground bg-muted/30 text-[10px] sm:text-xs"
                        >
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {t('moduleSelection.micNotSupported', {
                            defaultValue: 'Microphone not supported',
                          })}
                        </Badge>
                      )}
                    </div>
                  )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-center pt-6 px-4">
        <Button
          size="lg"
          onClick={handleConfirm}
          disabled={!canConfirm || isRequestingPermission}
          className="w-full sm:w-auto sm:min-w-[240px] font-bold py-6 text-base shadow-lg shadow-primary/10 transition-all active:scale-95"
        >
          {isRequestingPermission
            ? t('moduleSelection.requesting', {
                defaultValue: 'Requesting Permission...',
              })
            : t('moduleSelection.confirm', {
                defaultValue: 'Confirm Selection',
              })}
        </Button>
      </div>
    </div>
  );
}
